/* eslint-disable react/jsx-no-bind */
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { readOnlyText } from '@constants/mui-theme';
import Divider from '@material-ui/core/Divider';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import { itemFormatters } from '@utils/data-detail-utils';
import { sortByField } from '@utils/data-types-utils';
// TODO: Must use this, when MenuItem is moved to the new Material UI version.
// import { buildFilterMenuItem } from '@utils/filter-utils';
import { makeStyles } from '@material-ui/core/styles';
import SharedSelectField from '@shared/select-field';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import CheckIcon from '@material-ui/icons/Check';
import * as colors from '@constants/colors';


const DataTypesSelect = (props) => {

  const useStyles = makeStyles(() => ({
    menuItem: {
      backgroundColor: '#fff'
    },
    selected: {
      backgroundColor: '#fff',
      color: colors.dotmapsBlue,
      '&.Mui-selected': {
        backgroundColor: '#fff',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)'
        }
      }
    },
    gutters: {
      paddingLeft: '3rem'
    },
    checked: {
      minWidth: '2rem'
    }
  }));

  const classes = useStyles();

  const dataTypes = useSelector(state => state.dataTypes);
  const { map_category_type: categoryTypes } = dataTypes;

  let options;
  if (props.dataSource) {
    options = props.dataSource;
  } else {
    options = dataTypes[props.dataName];
  }
  if (categoryTypes && props.name?.startsWith('|')) {
    const categoryTypeName = props.name.substring(1);
    const categoryType = parseInt(Object.keys(categoryTypes).find(key => categoryTypes[key].name === categoryTypeName), 10);
    options = R.pickBy(option => option.type === categoryType, options);

    // If this field is required, don't allow users to see and set the '(blank)/No Designation' value.
    //
    // Also do the same for the special 'priority' category, since it already contains a 'No priority' value,
    // which is used by default when no priority is set.
    if (props.required || categoryTypeName === 'priority') {
      options = R.pickBy(option => option.id > 0, options);
    }
  }

  // The primary text is usually 'option.name', but this was extended
  // to allow using a custom item formatter.
  const getPrimaryText = option => {
    const { customItemFormatter } = props;
    if (customItemFormatter) {
      if (typeof customItemFormatter === 'string') {
        return itemFormatters[customItemFormatter](option);
      }
      return customItemFormatter(option.id);
    }
    return option.name;
  };

  const getSortedOptions = () => {
    const { dataSortField } = props;
    // If the options dataset has an active flag, we need to filter out the inactive options
    // specifically added to removed deleted agencies.
    // const filteredOptions = R.pickBy(option => ((!Object.prototype.hasOwnProperty.call(option, 'active') || option.active)), options);
    const filteredOptions = R.pickBy(option => (
      (!Object.prototype.hasOwnProperty.call(option, 'active') || option.active) && option.name !== 'No Designation'), options);
    let sortedOptions = sortByField(dataSortField || 'name')(R.values(filteredOptions));
    sortedOptions = sortedOptions.concat(R.values(R.pickBy(option => option.name === 'No Designation', options)));

    // Return the sorted options taking care of putting the 'unassigned/None' option first:
    const unassignedOption = sortedOptions.find(option => option.is_unassigned);
    if (unassignedOption) {
      return [
        unassignedOption,
        ...sortedOptions.filter(option => !option.is_unassigned)
      ];
    }
    return sortedOptions;
  };

  const getChecked = item => {
    const { value } = props;
    return value && value.includes(item.id);
  };

  const getPromoted = items => {
    const { value } = props;
    // Show the promoted section if there are selected values:
    if (value && value.length > 0) {
      const promotedItems = R.values(items).filter(item => getChecked(item));
      const demotedItems = R.values(items).filter(item => !getChecked(item));
      return {
        demotedItems,
        promotedItems,
        showPromoted: true
      };
    }
    return { showPromoted: false };
  };

  const getAllMenuItemLabel = items => {
    const { value } = props;
    if (items && value && items.length > 0 && items.length === value.length) {
      return 'UNSELECT ALL';
    }
    return 'SELECT ALL';
  };

  const onSelectChange = (event, child) => {
    const { multiple, value } = props;
    if (multiple && child.props.name === 'all') {
      const items = getSortedOptions();
      // If there's any item selected (but not all selected),
      // or if there's no item selected, let's select all.
      if (!value ||  // If nothing selected yet (value is null).
        items && (
          (value.length > 0 && value.length !== items.length) ||  // Any item selected but not all.
          value.length === 0  // Again nothing selected yet (value is []).
        )
      ) {
        const allIds = items.map(item => item.id);
        props.onChange(allIds);
        return;
      }
      // Else unselect all
      props.onChange(null);
    } else {
      props.onChange(event.target.value);
    }
  };

  const buildMenuItem = item => {
    const checked = getChecked(item);
    return (
      <MenuItem
        key={item.id}
        value={item.id}
        style={item.name === 'No Designation' ? { fontStyle: 'italic' } : {}}
        classes={{
          selected: classes.selected,
          gutters: !checked ? classes.gutters : {}
        }}
      >
        {checked && 
          <ListItemIcon classes={{root: classes.checked}}>
            <CheckIcon fontSize="small" />
          </ListItemIcon>
        }
        {item.name}
      </MenuItem>
    );
  };

  const getMenuItems = () => {
    const { multiple } = props;
    const items = getSortedOptions();

    if (multiple) {
      const { demotedItems, promotedItems, showPromoted } = getPromoted(items);
      let menuItems = [
        <MenuItem
          key="all"
          name="all"
          style={{ fontWeight: 500 }}
        >
          {getAllMenuItemLabel(items)}
        </MenuItem>

      ];
      // When the dropdown is opened, it must show the selected options in a promoted place:
      if (showPromoted) {
        menuItems = [
          ...menuItems,
          ...promotedItems.map(item => buildMenuItem(item))
        ];
        if (!R.isEmpty(promotedItems) && !R.isEmpty(demotedItems)) {
          menuItems.push(<Divider key="divider" />);
        }
        menuItems = [
          ...menuItems,
          ...demotedItems.map(item => buildMenuItem(item))
        ];
      } else {
        // When working normally, show all value without putting them on the promotion zone:
        menuItems = [
          ...menuItems,
          ...items.map(item => buildMenuItem(item))
        ];
      }

      return menuItems;
    }
    return items.map(option => (
      <MenuItem
        key={option.id}
        name={option.id}
        value={option.id}
        disabled={option.is_unassigned && !option.select_unassigned}
        style={option.name === 'No Designation' ? { fontStyle: 'italic' } : {}}
        classes={{
          root: classes.menuItem,
          selected: classes.selected
        }}
      >
        {getPrimaryText(option)}
      </MenuItem>
    ));
  };

  const selectionRenderer = (values) => {
    const menuItems = getMenuItems();
    const { multiple } = props;
    if (multiple) {
      // If all items are selected, display some 'All xxxx' label instead of the selected options.
      {
        const items = getSortedOptions();
        const { all } = props;
        if (all && all.label && items.length > 0 && items.length === values.length) {
          return all.label;
        }
      }
      // Else render each selected option separated by comma:
      const multipleValues = [];
      menuItems.forEach(item => {
        if (values.indexOf(parseInt(item.key, 10)) > -1) {
          multipleValues.push(item.props.label || item.props.children[1]);
        }
      });
      return multipleValues.join(', ');
    }

    // For single select, find the selected menu item and get its text content
    if (values !== null && values !== undefined) {  // eslint-disable-line no-undefined
      const selectedMenuItem = menuItems.find(item => item.key === values.toString());
      if (selectedMenuItem) {
        // Get the text content from the menu item's children
        if (typeof selectedMenuItem.props.children === 'string') {
          return selectedMenuItem.props.children;
        }
        // If children is an array (with icon), get the text part
        if (Array.isArray(selectedMenuItem.props.children) && selectedMenuItem.props.children[1]) {
          return selectedMenuItem.props.children[1];
        }
        return selectedMenuItem.props.children || '';
      }
    }
    return '';
  };

  const {
    all,  // eslint-disable-line no-unused-vars
    customItemFormatter,  // eslint-disable-line no-unused-vars
    dataName,  // eslint-disable-line no-unused-vars
    dataSource,  // eslint-disable-line no-unused-vars
    dataSortField,  // eslint-disable-line no-unused-vars
    errorStyle, // eslint-disable-line no-unused-vars
    floatingLabelStyle, // eslint-disable-line no-unused-vars
    onChange,  // eslint-disable-line no-unused-vars
    labelStyle, // eslint-disable-line no-unused-vars
    value,
    style,
    floatingLabelText,
    className,
    ...other
  } = props;
  const selectStyle = style ? style : {width: '100%'};
  return (
    <FormControl style={{ ...selectStyle}} focused={false}>
      {floatingLabelText && <InputLabel>{floatingLabelText}</InputLabel>}
      <SharedSelectField
        className={className}
        {...other}
        error={props.errorText}
        fullWidth
        displayEmpty
        selectStyles={{
          root: {
            marginLeft: '0',
            paddingLeft: '0',
            '&.Mui-disabled': readOnlyText
          }
        }}
        multiple={props.multiple}
        onChange={onSelectChange}
        value={!value && props.multiple ? [] : value}
        renderValue={selectionRenderer}
      >
        {getMenuItems()}
      </SharedSelectField>
      <FormHelperText error={props.errorText}>{props.errorText}</FormHelperText>
    </FormControl>
  );
};

DataTypesSelect.propTypes = {
  all: PropTypes.object,
  className: PropTypes.string,
  customItemFormatter: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  data: PropTypes.object,
  dataName: PropTypes.string,
  dataSortField: PropTypes.string,
  dataSource: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  errorStyle: PropTypes.object,
  errorText: PropTypes.string,
  floatingLabelStyle: PropTypes.object,
  floatingLabelText: PropTypes.string,
  labelStyle: PropTypes.object,
  multiple: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  required: PropTypes.bool,
  style: PropTypes.object,
  value: PropTypes.any  // eslint-disable-line react/forbid-prop-types
};

export default memo(DataTypesSelect);
